import {
  AuthenticationPageContainer,
  CheckInboxMessage,
  ForgotPasswordForm
} from '@/features/user/components'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useSendResetLink } from '@/features/user/hooks'
import { UnauthenticatedPage } from '@/components/UnauthenticatedPage/UnauthenticatedPage'
import { Avatar, Box, Typography, useTheme } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { violetVariants } from '@/themes/colors.ts'
import { useErrorMessageTranslation } from '@/hooks/useErrorMessageTranslation.ts'

export const Route = createFileRoute('/reset-password-expired')({
  component: ResetPasswordExpiredComponent
})

function ResetPasswordExpiredComponent() {
  const theme = useTheme()
  const { t } = useTranslation(['_routes', '_global'])
  const { translateError } = useErrorMessageTranslation()

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [userEmail, setUserEmail] = useState<string>('')
  const [currentStep, setCurrentStep] = useState<'enterEmail' | 'checkInbox'>(
    'enterEmail'
  )

  const { mutate: sendResetLink } = useSendResetLink({
    onSuccess: () => {
      setCurrentStep('checkInbox')
    },
    onError: error => {
      setErrorMessage(
        t('routes.reset-password-expired.main.otherError', {
          message: translateError(error)
        })
      )
    }
  })

  const handleSendResetLink = async (email?: string) => {
    if (email) {
      setIsLoading(true)
      sendResetLink(email)
      setUserEmail(email)
      setIsLoading(false)
    }
  }

  const handleResendEmail = async () => {
    if (userEmail) {
      sendResetLink(userEmail)
    }
  }

  const renderForgotPasswordForm = () => {
    if (currentStep === 'enterEmail') {
      return (
        <ForgotPasswordForm
          buttonTitle={t('routes.reset-password-expired.main.buttonTitle')}
          isLoading={isLoading}
          onSubmit={handleSendResetLink}
        />
      )
    }
  }

  return (
    <AuthenticationPageContainer
      advertisingChip={t('routes.reset-password-expired.main.advertising.chip')}
      advertisingTitle={t('routes.reset-password-expired.main.advertising.title')}
      advertisingSubtitle={t('routes.reset-password-expired.main.advertising.subTitle')}
    >
      {currentStep === 'enterEmail' && (
        <UnauthenticatedPage>
          <Box
            sx={{
              mx: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
              ml: '10%',
              mr: '30%'
            }}
          >
            <Avatar
              sx={{
                width: '50px',
                height: '50px',
                backgroundColor: violetVariants[100]
              }}
            >
              <WarningAmberOutlinedIcon sx={{ color: theme.palette.text.primary }} />
            </Avatar>

            <Typography sx={{ mt: 3 }} variant="h1" color="primary" component="span">
              {t('routes.reset-password-expired.main.title')}
            </Typography>

            <Typography sx={{ my: 2 }} variant="body2">
              {t('routes.reset-password-expired.main.infoBand')}
            </Typography>

            {renderForgotPasswordForm()}
          </Box>
        </UnauthenticatedPage>
      )}
      {currentStep === 'checkInbox' && (
        <CheckInboxMessage error={errorMessage} onSubmit={handleResendEmail} />
      )}
    </AuthenticationPageContainer>
  )
}
